/**
 * Mock Data for Approvals Section — Universal Human-in-the-Loop
 *
 * 10 diverse approval scenarios across departments:
 * Operations, Sales, Procurement, Safety, Compliance, IT, Communications
 */

// ── Types ──────────────────────────────────────────────────────────────────

export type ApprovalType = 'tool_call' | 'email_draft' | 'crm_response' | 'document' | 'access_change' | 'purchase_order' | 'notification' | 'custom';
export type ApprovalLevel = 'single' | 'supervisor' | 'two_person';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'edited' | 'expired';
export type ApprovalPriority = 'low' | 'medium' | 'high' | 'critical';

export interface ApprovalAgent {
  id: string;
  name: string;
  icon: string;
  iconColor: string;
}

export interface ReasoningStep {
  step: number;
  action: string;
  result: string;
  toolUsed?: string;
}

export interface HistoryEvent {
  timestamp: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'work_order' | 'alarm' | 'reading' | 'change' | 'comment' | 'commit' | 'message';
  title: string;
  description?: string;
  actor?: string;
}

export interface ApprovalEvidence {
  conversationTitle?: string;
  userQuery?: string;
  parameters?: Record<string, unknown>;
  preview?: string;
  diff?: { before: string; after: string };
  relatedRecords?: { label: string; value: string }[];
  riskFactors?: string[];
  recipients?: string[];
  subject?: string;
  // Decision context
  reasoningChain?: ReasoningStep[];
  confidence?: number;
  alternativesConsidered?: string[];
  sourcesCited?: string[];
  // Context history (right panel)
  contextHistory?: HistoryEvent[];
  contextSummary?: { label: string; value: string }[];
}

export interface ApprovalRequest {
  id: string;
  type: ApprovalType;
  agent: ApprovalAgent;
  toolName: string;
  toolDisplayName: string;
  action: {
    title: string;
    description: string;
  };
  evidence: ApprovalEvidence;
  level: ApprovalLevel;
  status: ApprovalStatus;
  priority: ApprovalPriority;
  riskScore: number;
  requestedBy: string;
  requestedAt: string;
  expiresAt: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionReason?: string;
  // For two_person rule
  approvals?: { userId: string; approvedAt: string }[];
  requiredApprovals?: number;
}

// ── Label Maps ─────────────────────────────────────────────────────────────

export const APPROVAL_TYPE_LABELS: Record<ApprovalType, { label: string; icon: string; color: string }> = {
  tool_call: { label: 'Tool Call', icon: 'Wrench', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  email_draft: { label: 'Email', icon: 'Mail', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  crm_response: { label: 'CRM', icon: 'Users', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  document: { label: 'Document', icon: 'FileText', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  access_change: { label: 'Access', icon: 'Shield', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  purchase_order: { label: 'Purchase', icon: 'CreditCard', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  notification: { label: 'Notification', icon: 'Bell', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' },
  custom: { label: 'Custom', icon: 'Zap', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

export const APPROVAL_LEVEL_STYLES: Record<ApprovalLevel, { label: string; color: string }> = {
  single: { label: 'Single Confirm', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  supervisor: { label: 'Supervisor', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  two_person: { label: '2-Person Rule', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
};

export const APPROVAL_STATUS_STYLES: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  approved: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
  edited: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  expired: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400',
};

export const APPROVAL_PRIORITY_STYLES: Record<ApprovalPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  medium: { label: 'Medium', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  high: { label: 'High', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  critical: { label: 'Critical', color: 'bg-red-500/10 text-red-600 dark:text-red-400' },
};

// ── Helper ─────────────────────────────────────────────────────────────────

function hoursFromNow(h: number): string {
  return new Date(Date.now() + h * 3600000).toISOString();
}

function hoursAgo(h: number): string {
  return new Date(Date.now() - h * 3600000).toISOString();
}

function minsAgo(m: number): string {
  return new Date(Date.now() - m * 60000).toISOString();
}

// ── Mock Data — 10 Diverse Scenarios ───────────────────────────────────────

export const MOCK_PENDING_APPROVALS: ApprovalRequest[] = [
  {
    id: 'apr-1',
    type: 'tool_call',
    agent: { id: 'agent-work-order', name: 'Work Order Planner', icon: 'clipboard-check', iconColor: 'amber' },
    toolName: 'iq.cmms.createWorkOrder',
    toolDisplayName: 'Create Work Order',
    action: { title: 'Create Work Order for Pump P-401', description: 'Corrective maintenance work order for bearing replacement based on vibration analysis showing 50% increase over 30 days.' },
    evidence: {
      conversationTitle: 'Vibration analysis for pump P-401',
      parameters: { assetTag: 'P-401', type: 'Corrective', priority: 'High', description: 'Bearing replacement — vibration RMS 4.2mm/s (alarm at 4.5)', estimatedHours: 8 },
      relatedRecords: [
        { label: 'Last PM', value: '2026-03-15 (33 days ago)' },
        { label: 'Vibration Trend', value: '2.8 → 4.2 mm/s over 30 days' },
        { label: 'Similar WO History', value: 'WO-2847 (2025-11) — same pump, same issue' },
      ],
      riskFactors: ['Equipment criticality: High', 'Vibration approaching alarm threshold'],
      userQuery: 'What is the vibration trend on pump P-401? Should we schedule maintenance?',
      reasoningChain: [
        { step: 1, action: 'Searched asset registry for pump P-401', result: 'Found: Centrifugal pump, Boiler Room A, Criticality: High', toolUsed: 'iq.asset.search' },
        { step: 2, action: 'Retrieved 30-day vibration trend data', result: 'RMS velocity increased from 2.8 → 4.2 mm/s (50% increase)', toolUsed: 'iq.telemetry.getHistory' },
        { step: 3, action: 'Compared against ISO 10816 thresholds', result: 'Current: 4.2 mm/s — Zone C (Alert). Alarm threshold: 4.5 mm/s', toolUsed: 'builtin.search_knowledge' },
        { step: 4, action: 'Checked maintenance history', result: 'Last PM: 33 days ago. Found similar incident WO-2847 (Nov 2025) — same pump, bearing failure', toolUsed: 'iq.asset.getHistory' },
        { step: 5, action: 'Generated recommendation', result: 'Bearing degradation likely based on trend + history. Recommend corrective WO before failure.', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.87,
      alternativesConsidered: [
        'Wait for next scheduled PM (Apr 25) — rejected: vibration may exceed alarm by then',
        'Order vibration analysis from contractor — rejected: sufficient internal data available',
        'Reduce pump speed to extend bearing life — considered but not primary recommendation',
      ],
      sourcesCited: [
        'Vibration telemetry data (iq.telemetry) — 30-day trend for P-401',
        'Work order WO-2847 (Nov 2025) — historical bearing replacement on same pump',
        'ISO 10816 Vibration Severity Chart — Zone classification',
        'PM schedule — last PM date and next scheduled PM',
      ],
      contextHistory: [
        { timestamp: '2026-04-18T08:00:00Z', type: 'reading', title: 'Vibration reading: 4.2 mm/s RMS', description: 'Zone C (Alert) — approaching alarm threshold of 4.5', actor: 'Sensor P-401-VIB' },
        { timestamp: '2026-04-15T10:00:00Z', type: 'reading', title: 'Vibration reading: 3.8 mm/s RMS', description: 'Zone B (Satisfactory) — trending upward', actor: 'Sensor P-401-VIB' },
        { timestamp: '2026-04-10T14:00:00Z', type: 'reading', title: 'Vibration reading: 3.4 mm/s RMS', description: 'Zone B (Satisfactory)', actor: 'Sensor P-401-VIB' },
        { timestamp: '2026-03-25T09:00:00Z', type: 'work_order', title: 'WO-3001: Preventive Maintenance completed', description: 'Lubrication, alignment check, filter replacement', actor: 'Tech: Mike Chen' },
        { timestamp: '2025-11-15T11:00:00Z', type: 'work_order', title: 'WO-2847: Bearing replacement', description: 'Replaced SKF 6205-2RS bearings — vibration was 4.8 mm/s', actor: 'Tech: Tom Wilson' },
        { timestamp: '2025-11-10T08:00:00Z', type: 'alarm', title: 'High vibration alarm triggered', description: 'P-401 vibration exceeded 4.5 mm/s threshold', actor: 'System' },
      ],
      contextSummary: [
        { label: 'Equipment', value: 'P-401 — Centrifugal Pump, Boiler Room A' },
        { label: 'Criticality', value: 'High — feeds boiler water supply' },
        { label: 'Age', value: '8 years (installed 2018)' },
        { label: 'MTBF', value: '14 months (last 3 failures)' },
        { label: 'Last PM', value: 'Mar 25, 2026 (24 days ago)' },
        { label: 'Next PM', value: 'Apr 25, 2026 (in 7 days)' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'high',
    riskScore: 0.65,
    requestedBy: 'Jay Talreja',
    requestedAt: minsAgo(12),
    expiresAt: hoursFromNow(4),
  },
  {
    id: 'apr-2',
    type: 'email_draft',
    agent: { id: 'agent-sales', name: 'Sales Assistant', icon: 'mail', iconColor: 'violet' },
    toolName: 'm365.outlook.sendEmail',
    toolDisplayName: 'Send Email',
    action: { title: 'Send follow-up email to Acme Industries', description: 'AI-drafted follow-up email to John Smith at Acme Industries regarding their RFQ for industrial sensors.' },
    evidence: {
      conversationTitle: 'Draft follow-up for Acme RFQ',
      recipients: ['john.smith@acme-industries.com'],
      subject: 'Re: RFQ-2026-0847 — Industrial Sensor Package',
      preview: `Dear John,\n\nThank you for your interest in our industrial sensor package. I've reviewed your requirements and wanted to share some key points:\n\n1. **Temperature Sensors**: We can provide Pt100 RTDs rated for -200°C to +850°C, exceeding your spec requirement.\n\n2. **Vibration Monitoring**: Our accelerometers offer 0-20kHz frequency response with ±2% accuracy.\n\n3. **Delivery Timeline**: We can ship within 4-6 weeks from PO receipt.\n\nI've attached our detailed technical specifications and pricing sheet. Would you be available for a call this Thursday to discuss?\n\nBest regards,\nArgus AI (on behalf of Sales Team)`,
      relatedRecords: [
        { label: 'Lead Value', value: '$145,000' },
        { label: 'Last Contact', value: '5 days ago (initial inquiry)' },
        { label: 'Company', value: 'Acme Industries — existing customer' },
      ],
      userQuery: 'Draft a follow-up email to Acme Industries about their sensor RFQ',
      reasoningChain: [
        { step: 1, action: 'Retrieved lead and RFQ details from CRM', result: 'RFQ-2026-0847: Industrial sensor package, $145K value', toolUsed: 'zoho.bigin.getLead' },
        { step: 2, action: 'Searched product catalog for matching specs', result: 'Found Pt100 RTDs and accelerometers matching their requirements', toolUsed: 'builtin.search_knowledge' },
        { step: 3, action: 'Checked pricing and availability', result: 'Standard pricing applies, 4-6 week delivery', toolUsed: 'ext.sap.checkStock' },
        { step: 4, action: 'Drafted personalized follow-up email', result: 'Professional email addressing 3 key spec points with pricing attachment offer', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.92,
      sourcesCited: [
        'CRM record: Acme Industries, RFQ-2026-0847',
        'Product catalog: Pt100 RTD specifications',
        'Product catalog: Accelerometer specifications',
        'SAP inventory: stock availability check',
      ],
      contextHistory: [
        { timestamp: '2026-04-13T14:30:00Z', type: 'email', title: 'Initial RFQ received from John Smith', description: 'Subject: Industrial Sensor Package Inquiry — requesting specs and pricing for 50-unit order', actor: 'john.smith@acme-industries.com' },
        { timestamp: '2026-04-13T15:00:00Z', type: 'note', title: 'Lead created in CRM', description: 'Auto-created from email. Deal value: $145K. Stage: New Lead', actor: 'System' },
        { timestamp: '2026-04-14T09:00:00Z', type: 'call', title: 'Brief intro call with John', description: 'Confirmed requirements: 50x temp sensors + 20x accelerometers. Timeline: Q3 2026', actor: 'Sales Team' },
      ],
      contextSummary: [
        { label: 'Contact', value: 'John Smith — VP Engineering, Acme Industries' },
        { label: 'Company', value: 'Acme Industries — existing customer since 2023' },
        { label: 'Deal Value', value: '$145,000 (50x sensors + 20x accelerometers)' },
        { label: 'Stage', value: 'Qualification → Proposal (pending this email)' },
        { label: 'Last Contact', value: '5 days ago (intro call)' },
        { label: 'Win Probability', value: '65% (existing relationship)' },
      ],
    },
    level: 'single',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.3,
    requestedBy: 'Sales Team',
    requestedAt: minsAgo(25),
    expiresAt: hoursFromNow(1),
  },
  {
    id: 'apr-3',
    type: 'crm_response',
    agent: { id: 'agent-crm', name: 'Lead Manager', icon: 'target', iconColor: 'emerald' },
    toolName: 'zoho.bigin.updateLead',
    toolDisplayName: 'Update Lead & Send Response',
    action: { title: 'Respond to Pinnacle Engineering inquiry', description: 'Research-backed response to a new lead about SCADA integration capabilities, based on Library documentation.' },
    evidence: {
      conversationTitle: 'New lead: Pinnacle Engineering — SCADA inquiry',
      preview: `Hi Sarah,\n\nThank you for reaching out about SCADA integration. Based on our capabilities:\n\n- We support **OPC-UA, Modbus TCP, and DNP3** protocols\n- Our platform integrates with **OSIsoft PI, Honeywell PHD, and GE Proficy Historian**\n- We offer **real-time data ingestion** at up to 10,000 tags/second\n\nI've attached our SCADA Integration Guide which covers architecture options for your plant configuration.\n\nWould Tuesday at 2 PM work for a technical deep-dive call?\n\nBest,\nArgus AI (Lead Management)`,
      parameters: { leadId: 'LM-0293', stage: 'Qualification', source: 'Website Form' },
      relatedRecords: [
        { label: 'Lead Score', value: '72/100 (High)' },
        { label: 'Company Size', value: '200-500 employees' },
        { label: 'Industry', value: 'Oil & Gas' },
      ],
      riskFactors: ['First contact — sets tone for relationship'],
      userQuery: 'Research and respond to the new SCADA integration inquiry from Pinnacle Engineering',
      reasoningChain: [
        { step: 1, action: 'Retrieved lead details from CRM', result: 'Pinnacle Engineering, Oil & Gas, 200-500 employees, score 72/100', toolUsed: 'zoho.bigin.getLead' },
        { step: 2, action: 'Searched Library for SCADA capabilities', result: 'Found SCADA Integration Guide with OPC-UA, Modbus, DNP3 support', toolUsed: 'builtin.search_knowledge' },
        { step: 3, action: 'Checked historian integration docs', result: 'Confirmed OSIsoft PI, Honeywell PHD, GE Proficy support', toolUsed: 'builtin.search_knowledge' },
        { step: 4, action: 'Composed response with technical details', result: 'Addressed SCADA protocols, historian support, and throughput capabilities', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.85,
      alternativesConsidered: [
        'Generic capabilities overview — rejected: lead scored high, deserves detailed technical response',
        'Schedule call without email — rejected: email with specifics first shows competence',
      ],
      sourcesCited: [
        'Library: SCADA Integration Guide v2.1',
        'Library: Historian Connectivity Matrix',
        'CRM: Pinnacle Engineering lead record (LM-0293)',
      ],
      contextHistory: [
        { timestamp: '2026-04-18T07:30:00Z', type: 'email', title: 'Website form submission', description: 'Sarah Chen submitted inquiry: "Looking for SCADA integration solution for our oil refinery operations"', actor: 'sarah.chen@pinnacle-eng.com' },
        { timestamp: '2026-04-18T07:35:00Z', type: 'note', title: 'Lead auto-scored at 72/100', description: 'High score due to: Oil & Gas industry match, company size 200-500, SCADA keyword', actor: 'System' },
      ],
      contextSummary: [
        { label: 'Contact', value: 'Sarah Chen — Operations Manager, Pinnacle Engineering' },
        { label: 'Company', value: 'Pinnacle Engineering — Oil & Gas, 200-500 employees' },
        { label: 'Lead Score', value: '72/100 (High)' },
        { label: 'Source', value: 'Website contact form' },
        { label: 'Inquiry', value: 'SCADA integration for oil refinery' },
        { label: 'Previous Relationship', value: 'None — new lead' },
      ],
    },
    level: 'single',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.35,
    requestedBy: 'Marketing',
    requestedAt: minsAgo(45),
    expiresAt: hoursFromNow(2),
  },
  {
    id: 'apr-4',
    type: 'purchase_order',
    agent: { id: 'agent-parts', name: 'Parts & Inventory', icon: 'database', iconColor: 'teal' },
    toolName: 'ext.sap.createPurchaseOrder',
    toolDisplayName: 'Create Purchase Order',
    action: { title: 'Order SKF bearings for pump P-401', description: 'Purchase 5x SKF 6205-2RS bearings from approved vendor. Current stock: 3 units, minimum: 5.' },
    evidence: {
      parameters: { vendor: 'SKF Authorized Distributor', partNumber: 'SKF 6205-2RS', quantity: 5, unitPrice: 42.50, totalAmount: 212.50, deliveryDays: 5 },
      relatedRecords: [
        { label: 'Current Stock', value: '3 units (min: 5)' },
        { label: 'Last Order', value: '2026-02-15 — 10 units @ $40.00/ea' },
        { label: 'Annual Usage', value: '~24 units (2 per month avg)' },
        { label: 'Budget Remaining', value: '$12,450 of $15,000 (FY26 MRO)' },
      ],
      riskFactors: ['Below minimum stock level', 'Critical equipment bearing'],
      userQuery: 'We need bearings for pump P-401 — order from our usual supplier',
      reasoningChain: [
        { step: 1, action: 'Checked current inventory for SKF 6205-2RS', result: 'Current stock: 3 units, minimum threshold: 5 units — below minimum', toolUsed: 'ext.sap.checkStock' },
        { step: 2, action: 'Retrieved historical purchase data', result: 'Last order: Feb 15, 10 units @ $40/ea from SKF Authorized Distributor', toolUsed: 'ext.sap.getPurchaseHistory' },
        { step: 3, action: 'Checked approved vendor list', result: 'SKF Authorized Distributor — approved, preferred vendor, 5-day lead time', toolUsed: 'ext.sap.getVendor' },
        { step: 4, action: 'Verified budget availability', result: 'FY26 MRO budget: $12,450 remaining of $15,000. PO amount $212.50 = 1.7% of remaining', toolUsed: 'ext.sap.getBudget' },
        { step: 5, action: 'Generated purchase order recommendation', result: 'Recommended 5 units to restore minimum stock. Price increase +$2.50/unit vs last order (6.25%)', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.91,
      alternativesConsidered: [
        'Order 10 units (bulk discount) — rejected: would exceed quarterly MRO spend rate',
        'Use alternative NTN 6205-2RS bearings — rejected: not yet qualified for P-401',
        'Wait for next scheduled bulk order (May 1) — rejected: stock below minimum now',
      ],
      sourcesCited: [
        'SAP Inventory: SKF 6205-2RS stock record',
        'Purchase history: PO-2026-0198 (Feb 15)',
        'Approved Vendor List: SKF Authorized Distributor',
        'FY26 MRO Budget Tracker',
      ],
      contextHistory: [
        { timestamp: '2026-04-18T07:00:00Z', type: 'alarm', title: 'Inventory below minimum threshold', description: 'SKF 6205-2RS dropped to 3 units (min: 5) after WO-3010 consumed 2 units', actor: 'System' },
        { timestamp: '2026-04-17T14:00:00Z', type: 'work_order', title: 'WO-3010: Used 2x SKF 6205-2RS', description: 'Bearing replacement on pump P-402', actor: 'Tech: Mike Chen' },
        { timestamp: '2026-02-15T10:00:00Z', type: 'note', title: 'Last PO for SKF bearings', description: 'PO-2026-0198: 10 units @ $40.00/ea, delivered Feb 20', actor: 'Procurement' },
        { timestamp: '2025-11-15T09:00:00Z', type: 'work_order', title: 'WO-2847: Used 2x SKF 6205-2RS', description: 'Bearing replacement on pump P-401', actor: 'Tech: Tom Wilson' },
      ],
      contextSummary: [
        { label: 'Part', value: 'SKF 6205-2RS — Deep groove ball bearing' },
        { label: 'Current Stock', value: '3 units (minimum: 5)' },
        { label: 'Monthly Usage', value: '~2 units/month (24/year avg)' },
        { label: 'Preferred Vendor', value: 'SKF Authorized Distributor — 5-day lead time' },
        { label: 'Last Price', value: '$40.00/unit (Feb 2026)' },
        { label: 'Budget Impact', value: '$212.50 of $12,450 remaining (1.7%)' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'high',
    riskScore: 0.5,
    requestedBy: 'Jay Talreja',
    requestedAt: hoursAgo(1),
    expiresAt: hoursFromNow(8),
  },
  {
    id: 'apr-5',
    type: 'tool_call',
    agent: { id: 'agent-safety', name: 'Safety & LOTO', icon: 'alert-triangle', iconColor: 'red' },
    toolName: 'iq.safety.issueLockout',
    toolDisplayName: 'Issue LOTO Permit',
    action: { title: 'LOTO permit for heat exchanger HX-301', description: 'Lockout/Tagout permit for tube bundle replacement. Requires isolation of steam, cooling water, and electrical supply.' },
    evidence: {
      conversationTitle: 'LOTO procedure for HX-301 maintenance',
      parameters: { equipment: 'HX-301', procedure: 'LOTO-HX-301-Rev3', isolationPoints: 4, lockoutType: 'Complex' },
      relatedRecords: [
        { label: 'Procedure', value: 'LOTO-HX-301-Rev3 (last reviewed 2026-01-20)' },
        { label: 'PPE Required', value: 'Hard hat, safety glasses, gloves, steel-toe boots' },
        { label: 'Isolation Points', value: 'Steam inlet (V-301), CW inlet (V-302), CW outlet (V-303), Electrical (MCC-3A)' },
      ],
      riskFactors: ['Safety-critical operation', 'Multiple isolation points', 'High-pressure steam system'],
      userQuery: 'I need a LOTO permit for tube bundle replacement on heat exchanger HX-301',
      reasoningChain: [
        { step: 1, action: 'Retrieved equipment details for HX-301', result: 'Shell & tube heat exchanger, Boiler Room A, steam/cooling water service, criticality: Critical', toolUsed: 'iq.asset.get' },
        { step: 2, action: 'Loaded LOTO procedure LOTO-HX-301-Rev3', result: '4 isolation points: steam inlet V-301, CW inlet V-302, CW outlet V-303, electrical MCC-3A', toolUsed: 'iq.document.get' },
        { step: 3, action: 'Verified procedure currency', result: 'LOTO-HX-301-Rev3 last reviewed 2026-01-20 — current and approved', toolUsed: 'iq.document.getVersion' },
        { step: 4, action: 'Checked for active work on connected equipment', result: 'No active LOTO permits on connected systems. Steam header B-101 is operational.', toolUsed: 'iq.safety.getActivePermits' },
        { step: 5, action: 'Verified operator qualifications', result: 'Mike Chen — LOTO authorized, training current (expires 2026-09-15)', toolUsed: 'iq.asset.getPersonnel' },
        { step: 6, action: 'Applied 2-person rule per safety policy', result: 'Complex lockout with >3 isolation points requires 2-person approval per Safety Policy SP-003', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.94,
      alternativesConsidered: [
        'Partial isolation (steam only) — rejected: cooling water must also be isolated for tube access',
        'Defer to next turnaround (June) — rejected: tube leak is active, risking cross-contamination',
        'Use simplified LOTO (< 3 points) — not applicable: 4 isolation points require complex procedure',
      ],
      sourcesCited: [
        'LOTO Procedure: LOTO-HX-301-Rev3 (reviewed 2026-01-20)',
        'Safety Policy SP-003: Complex Lockout Requirements',
        'OSHA 1910.147: Control of Hazardous Energy',
        'Equipment record: HX-301 P&ID drawing DWG-BR-A-003',
      ],
      contextHistory: [
        { timestamp: '2026-04-18T06:30:00Z', type: 'alarm', title: 'Tube leak detected on HX-301', description: 'Cooling water in condensate return — cross-contamination risk', actor: 'Sensor HX-301-LEAK' },
        { timestamp: '2026-04-18T06:45:00Z', type: 'work_order', title: 'WO-3014: Emergency tube bundle replacement', description: 'Created for HX-301 tube leak. Requires LOTO before work begins.', actor: 'Mike Chen' },
        { timestamp: '2026-04-18T07:00:00Z', type: 'note', title: 'Shift supervisor notified', description: 'Sarah Johnson informed of emergency repair. Approved work order priority.', actor: 'Mike Chen' },
        { timestamp: '2026-01-20T09:00:00Z', type: 'change', title: 'LOTO procedure Rev3 approved', description: 'Updated isolation point list after V-303 valve replacement', actor: 'Safety Committee' },
        { timestamp: '2025-10-05T08:00:00Z', type: 'work_order', title: 'WO-2790: Last tube inspection on HX-301', description: 'Ultrasonic thickness test — 2 tubes flagged for monitoring', actor: 'Tech: David Park' },
      ],
      contextSummary: [
        { label: 'Equipment', value: 'HX-301 — Shell & Tube Heat Exchanger, Boiler Room A' },
        { label: 'Service', value: 'Steam (150 PSI) / Cooling Water' },
        { label: 'Criticality', value: 'Critical — feeds boiler condensate return' },
        { label: 'Isolation Points', value: '4: V-301 (steam), V-302 (CW in), V-303 (CW out), MCC-3A (elec)' },
        { label: 'PPE Required', value: 'Hard hat, safety glasses, gloves, steel-toe boots, face shield' },
        { label: 'Operator', value: 'Mike Chen — LOTO authorized (exp. 2026-09-15)' },
      ],
    },
    level: 'two_person',
    status: 'pending',
    priority: 'critical',
    riskScore: 0.85,
    requestedBy: 'Mike Chen',
    requestedAt: minsAgo(8),
    expiresAt: hoursFromNow(2),
    approvals: [{ userId: 'supervisor-1', approvedAt: minsAgo(3) }],
    requiredApprovals: 2,
  },
  {
    id: 'apr-6',
    type: 'document',
    agent: { id: 'agent-docs', name: 'Documentation & SOP', icon: 'file-text', iconColor: 'blue' },
    toolName: 'iq.document.publish',
    toolDisplayName: 'Publish Document',
    action: { title: 'Publish updated SOP-401 Rev 4.0', description: 'Updated standard operating procedure for pump P-401 maintenance. Changes include revised torque specs and updated safety warnings.' },
    evidence: {
      diff: { before: 'Torque spec: 45 Nm\nSafety: Standard PPE', after: 'Torque spec: 50 Nm (revised per manufacturer bulletin MB-2026-03)\nSafety: Standard PPE + face shield for bearing press operation' },
      parameters: { documentId: 'SOP-401', version: '4.0', previousVersion: '3.2', changedSections: 3 },
      relatedRecords: [
        { label: 'Author', value: 'Reliability Engineering' },
        { label: 'Reviewer', value: 'Safety Committee (pending)' },
        { label: 'Affected Equipment', value: 'P-401, P-402, P-403 (same pump model)' },
      ],
      riskFactors: ['Changes to torque specifications', 'New safety requirement added'],
      userQuery: 'Publish the updated SOP-401 with the new torque specs from the manufacturer bulletin',
      reasoningChain: [
        { step: 1, action: 'Retrieved current SOP-401 Rev 3.2', result: 'Standard operating procedure for P-401 pump maintenance, last updated 2025-08-10', toolUsed: 'iq.document.get' },
        { step: 2, action: 'Parsed manufacturer bulletin MB-2026-03', result: 'Revised torque specification from 45 Nm to 50 Nm for bearing housing bolts', toolUsed: 'builtin.search_knowledge' },
        { step: 3, action: 'Identified affected sections', result: '3 sections changed: Section 4.2 (Torque Specs), Section 5.1 (Assembly), Section 6.3 (Safety Warnings)', toolUsed: 'llm_reasoning' },
        { step: 4, action: 'Cross-referenced safety implications', result: 'New face shield requirement for bearing press operation added based on incident report IR-2026-002', toolUsed: 'builtin.search_knowledge' },
        { step: 5, action: 'Generated diff for review', result: 'Created before/after comparison for all 3 changed sections', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.88,
      alternativesConsidered: [
        'Minor revision (3.3) instead of major (4.0) — rejected: torque spec change is safety-critical',
        'Update only torque spec, defer safety warning — rejected: face shield requirement is from active incident report',
        'Wait for Safety Committee quarterly review — rejected: manufacturer bulletin requires immediate action',
      ],
      sourcesCited: [
        'Manufacturer Bulletin: MB-2026-03 (SKF bearing housing torque revision)',
        'Incident Report: IR-2026-002 (bearing press eye injury near-miss)',
        'Current SOP-401 Rev 3.2',
        'ANSI Z535.6 Safety Warning Standards',
      ],
      contextHistory: [
        { timestamp: '2026-04-16T11:00:00Z', type: 'note', title: 'Manufacturer bulletin MB-2026-03 received', description: 'SKF revised torque specifications for 6205-series bearing housings', actor: 'Reliability Engineering' },
        { timestamp: '2026-04-15T09:00:00Z', type: 'note', title: 'Incident report IR-2026-002 filed', description: 'Near-miss: metal fragment during bearing press operation — face shield recommended', actor: 'Safety Committee' },
        { timestamp: '2026-04-16T14:00:00Z', type: 'change', title: 'SOP-401 Rev 4.0 drafted', description: 'Updated torque specs (45→50 Nm) and added face shield requirement', actor: 'Reliability Engineering' },
        { timestamp: '2025-08-10T10:00:00Z', type: 'change', title: 'SOP-401 Rev 3.2 published', description: 'Previous version — minor formatting update', actor: 'Documentation Team' },
      ],
      contextSummary: [
        { label: 'Document', value: 'SOP-401 — Pump P-401 Maintenance Procedure' },
        { label: 'Version Change', value: 'Rev 3.2 → Rev 4.0 (major revision)' },
        { label: 'Sections Changed', value: '3 of 8 sections (Torque, Assembly, Safety)' },
        { label: 'Affected Equipment', value: 'P-401, P-402, P-403 (same pump model)' },
        { label: 'Trigger', value: 'Manufacturer bulletin MB-2026-03 + Incident IR-2026-002' },
        { label: 'Review Status', value: 'Safety Committee review pending' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.45,
    requestedBy: 'Reliability Team',
    requestedAt: hoursAgo(2),
    expiresAt: hoursFromNow(6),
  },
  {
    id: 'apr-7',
    type: 'access_change',
    agent: { id: 'agent-it', name: 'IT Assistant', icon: 'lock', iconColor: 'red' },
    toolName: 'iam.grantAccess',
    toolDisplayName: 'Grant Access',
    action: { title: 'Grant SCADA read access to new technician', description: 'New hire Tom Wilson needs read-only SCADA access for Boiler Room A monitoring during onboarding.' },
    evidence: {
      parameters: { userId: 'tom.wilson@company.com', resource: 'SCADA — Boiler Room A', permissions: 'Read-Only', duration: '90 days (probation period)', requestedBy: 'Manager: Sarah Johnson' },
      relatedRecords: [
        { label: 'Employee', value: 'Tom Wilson — Maintenance Technician (new hire)' },
        { label: 'Start Date', value: '2026-04-15' },
        { label: 'Department', value: 'Maintenance — Boiler Operations' },
        { label: 'Background Check', value: 'Completed 2026-04-10' },
      ],
      riskFactors: ['New employee — probation period', 'SCADA system access'],
      userQuery: 'Set up SCADA monitoring access for our new maintenance technician Tom Wilson',
      reasoningChain: [
        { step: 1, action: 'Verified employee record for Tom Wilson', result: 'New hire, start date 2026-04-15, Maintenance Technician, reports to Sarah Johnson', toolUsed: 'iam.getUser' },
        { step: 2, action: 'Checked background and clearance status', result: 'Background check completed 2026-04-10 — cleared. No security concerns.', toolUsed: 'iam.getCompliance' },
        { step: 3, action: 'Reviewed role-based access template', result: 'Maintenance Technician role → SCADA read-only for assigned areas. Boiler Room A is assigned area.', toolUsed: 'iam.getRoleTemplate' },
        { step: 4, action: 'Checked existing SCADA access grants for Boiler Room A', result: '4 other technicians have read-only access. No conflicts.', toolUsed: 'iam.getResourceAccess' },
        { step: 5, action: 'Applied probation period policy', result: 'New hire policy: 90-day limited access with auto-review at day 90', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.89,
      alternativesConsidered: [
        'Full read-write SCADA access — rejected: probation period policy requires read-only first',
        'Access to all areas — rejected: role template limits to assigned area only',
        'Permanent access grant — rejected: 90-day review required for new hires per IT Policy IT-SEC-004',
      ],
      sourcesCited: [
        'HR Record: Tom Wilson (employee ID: EMP-2026-0089)',
        'IT Policy IT-SEC-004: New Hire Access Provisioning',
        'Role Template: Maintenance Technician — SCADA Access Matrix',
        'Background check report (completed 2026-04-10)',
      ],
      contextHistory: [
        { timestamp: '2026-04-15T08:00:00Z', type: 'note', title: 'Tom Wilson — first day orientation', description: 'Completed safety orientation, assigned to Boiler Room A team', actor: 'HR' },
        { timestamp: '2026-04-15T10:00:00Z', type: 'note', title: 'Manager access request submitted', description: 'Sarah Johnson requested SCADA read access for Tom Wilson', actor: 'Sarah Johnson' },
        { timestamp: '2026-04-10T14:00:00Z', type: 'note', title: 'Background check cleared', description: 'Standard employment verification and criminal check — no issues', actor: 'HR / Security' },
        { timestamp: '2026-04-08T09:00:00Z', type: 'note', title: 'Offer accepted', description: 'Tom Wilson accepted Maintenance Technician position', actor: 'HR' },
      ],
      contextSummary: [
        { label: 'Employee', value: 'Tom Wilson — Maintenance Technician (new hire)' },
        { label: 'Manager', value: 'Sarah Johnson — Maintenance Supervisor' },
        { label: 'Start Date', value: 'April 15, 2026 (3 days ago)' },
        { label: 'Resource', value: 'SCADA — Boiler Room A (read-only)' },
        { label: 'Duration', value: '90 days (probation review at day 90)' },
        { label: 'Background Check', value: 'Completed and cleared (April 10)' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.55,
    requestedBy: 'Sarah Johnson',
    requestedAt: hoursAgo(3),
    expiresAt: hoursFromNow(8),
  },
  {
    id: 'apr-8',
    type: 'notification',
    agent: { id: 'agent-shift', name: 'Shift Handover', icon: 'notebook', iconColor: 'amber' },
    toolName: 'slack.sendMessage',
    toolDisplayName: 'Send Slack Message',
    action: { title: 'Post shift handover summary to #operations', description: 'Automated shift handover report for Night shift, Boiler Room A.' },
    evidence: {
      preview: `**Shift Handover — Night Shift (18:00-06:00)**\n**Boiler Room A — April 18, 2026**\n\n**Alarms:** 2 (both acknowledged)\n- 01:15 — High steam pressure B-101 (resolved, valve adjusted)\n- 04:30 — Low water level B-102 (resolved, makeup pump restarted)\n\n**Work Completed:** WO-3012 — Replaced pressure gauge on B-101\n\n**Open Items:** WO-3015 — Bearing inspection on FW pump (scheduled for Day shift)\n\n**Safety:** No incidents or near-misses.`,
      parameters: { channel: '#operations', messageType: 'shift_handover' },
      reasoningChain: [
        { step: 1, action: 'Collected alarm data for night shift window', result: '2 alarms between 18:00-06:00: high steam pressure B-101 (01:15), low water level B-102 (04:30)', toolUsed: 'iq.telemetry.getAlarms' },
        { step: 2, action: 'Retrieved work orders completed during shift', result: 'WO-3012 completed: pressure gauge replacement on B-101', toolUsed: 'iq.cmms.getWorkOrders' },
        { step: 3, action: 'Checked for open/pending work orders', result: 'WO-3015 open: bearing inspection on feedwater pump, scheduled for day shift', toolUsed: 'iq.cmms.getWorkOrders' },
        { step: 4, action: 'Checked safety incident log', result: 'No incidents or near-misses reported during night shift', toolUsed: 'iq.safety.getIncidents' },
        { step: 5, action: 'Composed handover report', result: 'Formatted shift summary with alarms, work completed, open items, and safety status', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.96,
      sourcesCited: [
        'Alarm log: Boiler Room A (18:00-06:00, April 17-18)',
        'CMMS: Work orders for night shift',
        'Safety log: Incident reports April 17-18',
      ],
      contextHistory: [
        { timestamp: '2026-04-18T01:15:00Z', type: 'alarm', title: 'High steam pressure on B-101', description: 'Pressure: 165 PSI (alarm at 160). Resolved: pressure relief valve adjusted.', actor: 'Operator: Night Shift' },
        { timestamp: '2026-04-18T03:00:00Z', type: 'work_order', title: 'WO-3012 completed', description: 'Replaced pressure gauge on B-101 — new gauge reads correctly', actor: 'Tech: Night Shift' },
        { timestamp: '2026-04-18T04:30:00Z', type: 'alarm', title: 'Low water level on B-102', description: 'Level dropped to 45% (alarm at 50%). Resolved: makeup pump restarted.', actor: 'Operator: Night Shift' },
        { timestamp: '2026-04-17T18:00:00Z', type: 'message', title: 'Night shift started', description: 'Crew: 2 operators, 1 technician. All areas normal at shift start.', actor: 'Shift Lead' },
      ],
      contextSummary: [
        { label: 'Shift', value: 'Night Shift (18:00-06:00), April 17-18' },
        { label: 'Area', value: 'Boiler Room A' },
        { label: 'Alarms', value: '2 (both acknowledged and resolved)' },
        { label: 'Work Completed', value: '1 work order (WO-3012)' },
        { label: 'Open Items', value: '1 (WO-3015 for day shift)' },
        { label: 'Safety', value: 'No incidents or near-misses' },
      ],
    },
    level: 'single',
    status: 'pending',
    priority: 'low',
    riskScore: 0.15,
    requestedBy: 'System (Scheduled)',
    requestedAt: minsAgo(5),
    expiresAt: hoursFromNow(1),
  },
  // ── Additional Enterprise Scenarios ──────────────────────────────────────
  {
    id: 'apr-9',
    type: 'custom',
    agent: { id: 'agent-hr', name: 'HR Onboarding', icon: 'user-plus', iconColor: 'pink' },
    toolName: 'ext.workday.createOnboarding',
    toolDisplayName: 'Create Onboarding Package',
    action: { title: 'Generate onboarding package for Maria Santos', description: 'Complete onboarding package including IT provisioning checklist, safety training schedule, and equipment assignment for new Process Engineer.' },
    evidence: {
      conversationTitle: 'New hire onboarding: Maria Santos — Process Engineer',
      parameters: { employeeId: 'EMP-2026-0091', department: 'Process Engineering', startDate: '2026-04-22', manager: 'David Park', office: 'Building A, 3rd Floor' },
      preview: `**Onboarding Package — Maria Santos**\n**Process Engineer, Start Date: April 22, 2026**\n\n**Week 1 Schedule:**\n- Day 1: Safety orientation, badge activation, IT setup\n- Day 2: Department introduction, mentor assignment (Tom Chen)\n- Day 3: Systems training (SCADA overview, CMMS access)\n- Day 4: Area walkthrough (Boiler Room A, Cooling Tower)\n- Day 5: First project briefing, goal setting with manager\n\n**Equipment:**\n- Laptop: Dell Precision 5680 (ordered)\n- PPE: Hard hat, safety glasses, steel-toe boots\n- Badge: Level 2 access (offices + controlled areas)\n\n**Training Certifications Required (90 days):**\n- Basic LOTO (within 30 days)\n- Confined Space Entry (within 60 days)\n- Hot Work Permit (within 90 days)`,
      relatedRecords: [
        { label: 'Position', value: 'Process Engineer — Grade P3' },
        { label: 'Manager', value: 'David Park — Sr. Process Engineer' },
        { label: 'Mentor', value: 'Tom Chen — Process Engineer (2 years)' },
        { label: 'Salary Band', value: 'P3: $95K-$115K (confidential)' },
      ],
      riskFactors: ['Equipment order lead time', 'Safety certifications required within 90 days'],
      userQuery: 'Create onboarding package for our new process engineer starting next week',
      reasoningChain: [
        { step: 1, action: 'Retrieved employee record from HR system', result: 'Maria Santos, Process Engineer, start date April 22, department: Process Engineering', toolUsed: 'ext.workday.getEmployee' },
        { step: 2, action: 'Loaded Process Engineer onboarding template', result: 'Standard template with 5-day orientation, 90-day certification plan, equipment list', toolUsed: 'builtin.search_knowledge' },
        { step: 3, action: 'Checked IT equipment availability', result: 'Dell Precision 5680 in stock, badge provisioning takes 1 business day', toolUsed: 'ext.servicenow.checkInventory' },
        { step: 4, action: 'Identified required safety certifications', result: '3 certifications required: LOTO (30d), Confined Space (60d), Hot Work (90d)', toolUsed: 'iq.safety.getCertRequirements' },
        { step: 5, action: 'Generated personalized onboarding schedule', result: 'Customized 5-day orientation + 90-day training plan based on role and area assignment', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.93,
      alternativesConsidered: [
        'Compressed 3-day orientation — rejected: safety certifications require dedicated training days',
        'Remote onboarding first week — rejected: field areas require in-person walkthrough',
      ],
      sourcesCited: [
        'HR Template: Process Engineer Onboarding v3.1',
        'IT Inventory: Equipment availability check',
        'Safety Training Matrix: Process Engineering roles',
      ],
      contextHistory: [
        { timestamp: '2026-04-14T10:00:00Z', type: 'note', title: 'Offer accepted by Maria Santos', description: 'Process Engineer position, start date confirmed April 22', actor: 'HR / Recruiting' },
        { timestamp: '2026-04-15T09:00:00Z', type: 'note', title: 'Background check completed', description: 'Standard verification — cleared, no issues', actor: 'HR / Security' },
        { timestamp: '2026-04-16T14:00:00Z', type: 'note', title: 'Manager notified of new hire', description: 'David Park confirmed mentor assignment: Tom Chen', actor: 'HR' },
      ],
      contextSummary: [
        { label: 'Employee', value: 'Maria Santos — Process Engineer' },
        { label: 'Start Date', value: 'April 22, 2026 (4 days)' },
        { label: 'Department', value: 'Process Engineering — Boiler Operations' },
        { label: 'Manager', value: 'David Park' },
        { label: 'Equipment', value: 'Laptop ordered, PPE assigned, badge pending' },
        { label: 'Certifications', value: '3 required within 90 days (LOTO, Confined Space, Hot Work)' },
      ],
    },
    level: 'single',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.25,
    requestedBy: 'HR / Recruiting',
    requestedAt: hoursAgo(4),
    expiresAt: hoursFromNow(24),
  },
  {
    id: 'apr-10',
    type: 'purchase_order',
    agent: { id: 'agent-finance', name: 'Budget & Finance', icon: 'calculator', iconColor: 'emerald' },
    toolName: 'ext.sap.createBudgetTransfer',
    toolDisplayName: 'Create Budget Transfer',
    action: { title: 'Transfer $15,000 from CapEx reserve to MRO budget', description: 'Budget reallocation to cover unplanned corrective maintenance costs for Q2. Current MRO budget 83% consumed with 2.5 months remaining.' },
    evidence: {
      conversationTitle: 'Q2 MRO budget analysis and reallocation request',
      parameters: { fromAccount: 'CapEx Reserve (ACC-7100)', toAccount: 'MRO Operations (ACC-5200)', amount: 15000, approvalLevel: 'Director', fiscalYear: 'FY26' },
      relatedRecords: [
        { label: 'MRO Budget (FY26)', value: '$180,000 allocated, $149,400 spent (83%)' },
        { label: 'CapEx Reserve', value: '$245,000 remaining (no committed projects in Q2)' },
        { label: 'Top Cost Drivers', value: 'Pump P-401 bearing ($4,200), HX-301 tube bundle ($8,500)' },
        { label: 'YoY Comparison', value: 'MRO spend +12% vs FY25 Q2 (driven by aging equipment)' },
      ],
      riskFactors: ['Budget 83% consumed with 2.5 months remaining', 'CapEx reserve reduction requires director approval'],
      userQuery: 'We are running low on MRO budget — can you analyze spend and recommend a reallocation?',
      reasoningChain: [
        { step: 1, action: 'Analyzed MRO budget consumption rate', result: 'FY26 MRO: $149,400 of $180,000 spent (83%) with 2.5 months remaining in fiscal year', toolUsed: 'ext.sap.getBudget' },
        { step: 2, action: 'Projected remaining MRO spend', result: 'Estimated $45,000 remaining spend based on historical rate + known upcoming work orders', toolUsed: 'ext.sap.getBudgetForecast' },
        { step: 3, action: 'Identified unplanned cost drivers', result: 'P-401 bearings ($4,200) and HX-301 tube bundle ($8,500) were unplanned corrective maintenance', toolUsed: 'iq.cmms.getCostReport' },
        { step: 4, action: 'Checked CapEx reserve availability', result: 'CapEx reserve: $245,000, no committed projects in Q2. $15,000 transfer reduces to $230,000.', toolUsed: 'ext.sap.getBudget' },
        { step: 5, action: 'Generated budget transfer recommendation', result: '$15,000 transfer covers projected shortfall with 10% margin. Requires director-level approval.', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.86,
      alternativesConsidered: [
        'Request emergency budget increase to CFO — rejected: CapEx reserve transfer is faster and within director authority',
        'Defer non-critical maintenance to Q3 — rejected: deferring PM work increases corrective maintenance risk',
        'Transfer $25,000 for larger buffer — rejected: $15,000 covers projected need with margin, smaller transfer is easier to approve',
      ],
      sourcesCited: [
        'SAP Budget Report: FY26 MRO (ACC-5200)',
        'SAP Budget Report: FY26 CapEx Reserve (ACC-7100)',
        'CMMS Cost Report: Q2 unplanned maintenance',
        'Finance Policy FP-003: Inter-account transfers',
      ],
      contextHistory: [
        { timestamp: '2026-04-17T16:00:00Z', type: 'alarm', title: 'MRO budget 80% consumed', description: 'Automated alert: MRO budget crossed 80% threshold with 2.5 months remaining', actor: 'System' },
        { timestamp: '2026-04-15T10:00:00Z', type: 'work_order', title: 'WO-3014 cost: $8,500', description: 'HX-301 tube bundle replacement — unplanned corrective maintenance', actor: 'Procurement' },
        { timestamp: '2026-04-10T14:00:00Z', type: 'work_order', title: 'WO-3010 cost: $4,200', description: 'P-401 bearing replacement + parts — unplanned corrective maintenance', actor: 'Procurement' },
        { timestamp: '2026-04-01T09:00:00Z', type: 'note', title: 'Q2 budget review', description: 'MRO spend on track until unplanned corrective work in April', actor: 'Finance' },
      ],
      contextSummary: [
        { label: 'From Account', value: 'CapEx Reserve (ACC-7100) — $245,000 available' },
        { label: 'To Account', value: 'MRO Operations (ACC-5200) — $30,600 remaining' },
        { label: 'Transfer Amount', value: '$15,000 (covers projected shortfall + 10% margin)' },
        { label: 'Approval Level', value: 'Director (per Finance Policy FP-003)' },
        { label: 'YoY Trend', value: 'MRO spend +12% vs FY25 (aging equipment factor)' },
        { label: 'Post-Transfer', value: 'MRO: $45,600 remaining, CapEx: $230,000 remaining' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'high',
    riskScore: 0.55,
    requestedBy: 'Operations Manager',
    requestedAt: hoursAgo(5),
    expiresAt: hoursFromNow(24),
  },
  {
    id: 'apr-11',
    type: 'tool_call',
    agent: { id: 'agent-it-change', name: 'IT Change Management', icon: 'git-branch', iconColor: 'indigo' },
    toolName: 'ext.servicenow.createChange',
    toolDisplayName: 'Create Change Request',
    action: { title: 'Deploy SCADA firmware update to Boiler Room A PLCs', description: 'Firmware update v4.2.1 for Siemens S7-1500 PLCs. Addresses CVE-2026-1847 (critical security vulnerability) and includes performance improvements.' },
    evidence: {
      conversationTitle: 'SCADA security patch deployment — CVE-2026-1847',
      parameters: { changeId: 'CHG-2026-0089', type: 'Standard', window: 'Saturday 02:00-06:00', rollbackPlan: 'Revert to v4.1.9 via USB recovery', affectedSystems: 6, downtime: '~15 minutes per PLC' },
      preview: `**Change Request: SCADA Firmware Update**\n\n**Scope:** 6 Siemens S7-1500 PLCs in Boiler Room A\n**Current Version:** v4.1.9\n**Target Version:** v4.2.1\n\n**Security Fix:** CVE-2026-1847 — Remote code execution vulnerability in OPC-UA stack\n- CVSS Score: 9.1 (Critical)\n- Exploit: Public PoC available since April 10\n- Mitigation: Firmware update is the only permanent fix\n\n**Maintenance Window:** Saturday, April 19, 02:00-06:00\n**Expected Downtime:** ~15 minutes per PLC (sequential, not simultaneous)\n\n**Rollback Plan:** USB recovery to v4.1.9 — tested and validated\n\n**Pre-checks:** Backup PLC programs, verify firmware integrity (SHA-256), confirm rollback USB prepared`,
      relatedRecords: [
        { label: 'Vulnerability', value: 'CVE-2026-1847 — CVSS 9.1 (Critical)' },
        { label: 'Affected PLCs', value: '6x Siemens S7-1500 in Boiler Room A' },
        { label: 'Last Firmware Update', value: 'v4.1.9 (2025-11-15)' },
        { label: 'Vendor Advisory', value: 'Siemens SSA-2026-04 (published April 8)' },
      ],
      riskFactors: ['Critical security vulnerability with public exploit', 'PLC downtime during update', 'SCADA system modification'],
      userQuery: 'We need to patch the Siemens PLCs for the CVE-2026-1847 vulnerability — create a change request',
      reasoningChain: [
        { step: 1, action: 'Retrieved CVE details from vulnerability database', result: 'CVE-2026-1847: RCE in Siemens S7-1500 OPC-UA stack, CVSS 9.1, public PoC since April 10', toolUsed: 'builtin.search_knowledge' },
        { step: 2, action: 'Identified affected assets', result: '6 Siemens S7-1500 PLCs in Boiler Room A running firmware v4.1.9 (vulnerable)', toolUsed: 'iq.asset.search' },
        { step: 3, action: 'Retrieved vendor advisory and patch', result: 'Siemens SSA-2026-04: firmware v4.2.1 fixes CVE-2026-1847, validated for S7-1500 series', toolUsed: 'builtin.search_knowledge' },
        { step: 4, action: 'Identified optimal maintenance window', result: 'Saturday 02:00-06:00 — lowest production load, maintenance crew available', toolUsed: 'iq.cmms.getSchedule' },
        { step: 5, action: 'Prepared rollback plan', result: 'USB recovery to v4.1.9 tested on lab PLC — recovery time: 5 minutes per unit', toolUsed: 'llm_reasoning' },
        { step: 6, action: 'Generated change request for approval', result: 'Standard change with pre-approved rollback, sequential deployment to minimize risk', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.92,
      alternativesConsidered: [
        'Network segmentation only (no patch) — rejected: temporary mitigation, not a permanent fix',
        'Update all PLCs simultaneously — rejected: too risky, sequential minimizes blast radius',
        'Delay to next scheduled maintenance window (May 3) — rejected: public exploit available, unacceptable risk',
      ],
      sourcesCited: [
        'CVE-2026-1847: NVD entry',
        'Siemens Advisory SSA-2026-04',
        'ICS-CERT Alert: Siemens S7-1500 OPC-UA vulnerability',
        'Change Management Policy: IT-CHG-001',
      ],
      contextHistory: [
        { timestamp: '2026-04-08T12:00:00Z', type: 'alarm', title: 'Siemens advisory SSA-2026-04 published', description: 'Critical vulnerability in S7-1500 OPC-UA stack — firmware update required', actor: 'Vendor / CERT' },
        { timestamp: '2026-04-10T09:00:00Z', type: 'alarm', title: 'Public exploit (PoC) released', description: 'CVE-2026-1847 exploit code published on GitHub — immediate patching recommended', actor: 'Security Team' },
        { timestamp: '2026-04-12T14:00:00Z', type: 'note', title: 'Firmware v4.2.1 tested in lab', description: 'Validated on lab S7-1500: patch applies cleanly, no functional regressions', actor: 'IT / OT Team' },
        { timestamp: '2026-04-15T10:00:00Z', type: 'note', title: 'Rollback procedure validated', description: 'USB recovery to v4.1.9 tested — recovery time 5 minutes per PLC', actor: 'IT / OT Team' },
      ],
      contextSummary: [
        { label: 'Change', value: 'SCADA firmware update v4.1.9 → v4.2.1' },
        { label: 'Vulnerability', value: 'CVE-2026-1847 — CVSS 9.1 (Critical, public exploit)' },
        { label: 'Affected Systems', value: '6x Siemens S7-1500 PLCs, Boiler Room A' },
        { label: 'Window', value: 'Saturday April 19, 02:00-06:00' },
        { label: 'Downtime', value: '~15 min per PLC (sequential, ~90 min total)' },
        { label: 'Rollback', value: 'USB recovery to v4.1.9 (tested, 5 min/unit)' },
      ],
    },
    level: 'two_person',
    status: 'pending',
    priority: 'critical',
    riskScore: 0.75,
    requestedBy: 'IT Security',
    requestedAt: hoursAgo(6),
    expiresAt: hoursFromNow(18),
    approvals: [],
    requiredApprovals: 2,
  },
  {
    id: 'apr-12',
    type: 'document',
    agent: { id: 'agent-legal', name: 'Legal & Contracts', icon: 'scale', iconColor: 'slate' },
    toolName: 'ext.docusign.sendForSignature',
    toolDisplayName: 'Send for Signature',
    action: { title: 'Send vendor NDA to Apex Controls for signature', description: 'Standard mutual NDA for MCP server integration project. Apex Controls will develop a custom SCADA connector.' },
    evidence: {
      conversationTitle: 'NDA preparation for Apex Controls partnership',
      parameters: { documentType: 'Mutual NDA', counterparty: 'Apex Controls LLC', template: 'NDA-STD-2024-v3', duration: '3 years', jurisdiction: 'State of Texas' },
      preview: `**Non-Disclosure Agreement**\n**Parties:** Viaanix Inc. ↔ Apex Controls LLC\n\n**Purpose:** Protection of confidential information exchanged during the development of a custom SCADA MCP connector server.\n\n**Key Terms:**\n- **Duration:** 3 years from effective date\n- **Scope:** Technical specifications, API documentation, source code, business processes\n- **Exclusions:** Publicly available information, independently developed IP\n- **Jurisdiction:** State of Texas\n- **Return/Destroy:** Within 30 days of termination\n\n**Signatories:**\n- Viaanix: Jay Talreja, CTO\n- Apex Controls: Michael Torres, VP Engineering`,
      relatedRecords: [
        { label: 'Partner', value: 'Apex Controls LLC — Industrial automation company' },
        { label: 'Project', value: 'Custom SCADA MCP connector development' },
        { label: 'Estimated Value', value: '$85,000 (development contract to follow NDA)' },
        { label: 'Existing Relationship', value: 'New vendor — first engagement' },
      ],
      riskFactors: ['New vendor relationship', 'Sharing technical API documentation', 'IP protection required'],
      userQuery: 'Prepare and send the NDA to Apex Controls so we can start the SCADA connector project',
      reasoningChain: [
        { step: 1, action: 'Retrieved standard NDA template', result: 'NDA-STD-2024-v3: mutual NDA template, last reviewed by legal Jan 2026', toolUsed: 'builtin.search_knowledge' },
        { step: 2, action: 'Populated counterparty details', result: 'Apex Controls LLC, VP Engineering Michael Torres, Texas jurisdiction', toolUsed: 'ext.crm.getContact' },
        { step: 3, action: 'Checked for existing agreements', result: 'No existing NDAs or contracts with Apex Controls — first engagement', toolUsed: 'builtin.search_knowledge' },
        { step: 4, action: 'Reviewed project scope for IP sensitivity', result: 'MCP connector project involves API specs and internal architecture — NDA scope covers technical + business info', toolUsed: 'llm_reasoning' },
        { step: 5, action: 'Prepared DocuSign envelope', result: 'Document ready for signature: 2 signatories, 3-year term, Texas law governing', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.9,
      alternativesConsidered: [
        'One-way NDA (us disclosing only) — rejected: Apex will also share their SCADA expertise',
        'Shorter 1-year term — rejected: connector project timeline is 6-9 months, 3 years provides buffer',
      ],
      sourcesCited: [
        'Legal Template: NDA-STD-2024-v3',
        'CRM: Apex Controls contact record',
        'Project Proposal: SCADA MCP Connector (approved April 5)',
      ],
      contextHistory: [
        { timestamp: '2026-04-05T11:00:00Z', type: 'meeting', title: 'SCADA connector project approved', description: 'Leadership approved engaging Apex Controls for custom SCADA MCP connector', actor: 'Jay Talreja' },
        { timestamp: '2026-04-08T14:00:00Z', type: 'email', title: 'Initial contact with Apex Controls', description: 'Michael Torres confirmed interest, requested NDA before technical discussions', actor: 'Sales Team' },
        { timestamp: '2026-04-12T09:00:00Z', type: 'note', title: 'Legal reviewed project scope', description: 'Confirmed standard mutual NDA is appropriate for this engagement', actor: 'Legal' },
      ],
      contextSummary: [
        { label: 'Document', value: 'Mutual NDA — NDA-STD-2024-v3' },
        { label: 'Counterparty', value: 'Apex Controls LLC — Industrial automation' },
        { label: 'Project', value: 'Custom SCADA MCP connector development ($85K)' },
        { label: 'Term', value: '3 years, Texas jurisdiction' },
        { label: 'Signatories', value: 'Jay Talreja (Viaanix) + Michael Torres (Apex)' },
        { label: 'Legal Review', value: 'Standard template approved, no custom clauses needed' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.4,
    requestedBy: 'Business Development',
    requestedAt: hoursAgo(8),
    expiresAt: hoursFromNow(48),
  },
  {
    id: 'apr-13',
    type: 'email_draft',
    agent: { id: 'agent-marketing', name: 'Marketing Assistant', icon: 'megaphone', iconColor: 'orange' },
    toolName: 'm365.outlook.sendCampaign',
    toolDisplayName: 'Send Campaign Email',
    action: { title: 'Launch Q2 product update email to 847 customers', description: 'Quarterly product update email highlighting new SCADA integration, AI-powered maintenance forecasting, and upcoming webinar invitation.' },
    evidence: {
      conversationTitle: 'Q2 product update campaign draft',
      recipients: ['customer-list-active@mailchimp.com (847 recipients)'],
      subject: 'ArgusAI Q2 Update: SCADA Integration, AI Maintenance Forecasting & More',
      preview: `**ArgusAI Platform — Q2 2026 Product Update**\n\nDear valued customer,\n\nWe're excited to share the latest additions to the ArgusAI platform:\n\n**New: SCADA Integration**\nConnect your Siemens, Honeywell, or GE SCADA systems directly. OPC-UA, Modbus TCP, and DNP3 protocols supported. Real-time data at up to 10,000 tags/second.\n\n**New: AI Maintenance Forecasting**\nOur Maintenance Intelligence agent now predicts equipment failures 2-4 weeks in advance with 87% accuracy, based on vibration, temperature, and pressure trends.\n\n**Upcoming Webinar**\nJoin us May 8 at 2 PM EST for a live demo of the new SCADA integration.\n[Register Here]\n\n**Customer Spotlight:** Pinnacle Engineering reduced unplanned downtime by 34% in their first 90 days.\n\nBest regards,\nThe ArgusAI Team`,
      relatedRecords: [
        { label: 'Recipients', value: '847 active customers (segmented: industrial)' },
        { label: 'Open Rate (last campaign)', value: '32.4% (industry avg: 21%)' },
        { label: 'Click Rate (last campaign)', value: '8.7%' },
        { label: 'Unsubscribe Rate', value: '0.3% (healthy)' },
      ],
      riskFactors: ['Mass email to 847 customers', 'Contains product claims (accuracy %)'],
      userQuery: 'Draft and schedule the Q2 product update email for our active customer base',
      reasoningChain: [
        { step: 1, action: 'Retrieved active customer segment from CRM', result: '847 active customers in industrial segment, opted-in for product updates', toolUsed: 'ext.mailchimp.getSegment' },
        { step: 2, action: 'Gathered Q2 product updates from release notes', result: 'Key features: SCADA integration (GA), maintenance forecasting (87% accuracy claim validated)', toolUsed: 'builtin.search_knowledge' },
        { step: 3, action: 'Checked compliance for product claims', result: '87% accuracy claim validated by internal eval dataset (200+ predictions). Customer spotlight approved by Pinnacle.', toolUsed: 'builtin.search_knowledge' },
        { step: 4, action: 'Applied brand guidelines and email template', result: 'Used approved Q-update template, brand colors, responsive design tested', toolUsed: 'llm_reasoning' },
        { step: 5, action: 'Analyzed optimal send time', result: 'Tuesday 10 AM EST has highest historical open rate for this segment', toolUsed: 'ext.mailchimp.getAnalytics' },
      ],
      confidence: 0.88,
      alternativesConsidered: [
        'Separate emails per feature — rejected: quarterly digest format has higher engagement',
        'Include pricing changes — rejected: pricing update deserves separate communication',
        'Include customer spotlight without permission — rejected: Pinnacle approved usage on April 10',
      ],
      sourcesCited: [
        'Product Release Notes: Q2 2026',
        'Internal Eval: Maintenance forecasting accuracy report',
        'Customer Approval: Pinnacle Engineering spotlight (April 10)',
        'Mailchimp Analytics: Historical engagement data',
      ],
      contextHistory: [
        { timestamp: '2026-04-10T11:00:00Z', type: 'email', title: 'Pinnacle approved customer spotlight', description: 'Sarah Chen confirmed OK to use 34% downtime reduction stat in marketing', actor: 'Marketing' },
        { timestamp: '2026-04-12T14:00:00Z', type: 'note', title: 'Product claims verified', description: '87% forecasting accuracy validated against 200+ prediction eval set', actor: 'Product Team' },
        { timestamp: '2026-04-14T10:00:00Z', type: 'note', title: 'Q1 campaign performance review', description: 'Q1 update: 32.4% open rate, 8.7% click rate, 0.3% unsubscribe — all above benchmarks', actor: 'Marketing' },
      ],
      contextSummary: [
        { label: 'Campaign', value: 'Q2 2026 Product Update — Quarterly digest' },
        { label: 'Recipients', value: '847 active customers (industrial segment)' },
        { label: 'Key Features', value: 'SCADA integration, AI maintenance forecasting, webinar invite' },
        { label: 'Scheduled Send', value: 'Tuesday April 22, 10 AM EST (optimal time)' },
        { label: 'Previous Performance', value: '32.4% open rate, 8.7% CTR (above industry avg)' },
        { label: 'Claims Status', value: 'All product claims verified, spotlight approved' },
      ],
    },
    level: 'supervisor',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.45,
    requestedBy: 'Marketing Team',
    requestedAt: hoursAgo(12),
    expiresAt: hoursFromNow(36),
  },
  {
    id: 'apr-14',
    type: 'tool_call',
    agent: { id: 'agent-pm', name: 'Project Tracker', icon: 'kanban', iconColor: 'blue' },
    toolName: 'ext.jira.updateIssue',
    toolDisplayName: 'Update Project Milestone',
    action: { title: 'Close Sprint 14 and release v2.4.0 to staging', description: 'Sprint 14 complete: 18 of 19 stories done, 1 moved to backlog. Release v2.4.0 includes SCADA connector MVP and 3 bug fixes.' },
    evidence: {
      conversationTitle: 'Sprint 14 closure and release preparation',
      parameters: { sprintId: 'SPRINT-14', version: 'v2.4.0', storiesCompleted: 18, storiesTotal: 19, bugsFixed: 3, environment: 'staging' },
      preview: `**Sprint 14 Summary — v2.4.0 Release**\n\n**Velocity:** 47 story points (target: 45) ✅\n\n**Completed (18/19):**\n- SCADA connector MVP (OPC-UA + Modbus) — 13 SP\n- Bug: Memory leak in vector store connection pool — 5 SP\n- Bug: Chat history pagination off-by-one — 3 SP  \n- Bug: Agent timeout not respecting config — 5 SP\n- 14 smaller stories — 21 SP\n\n**Moved to Backlog (1):**\n- DNP3 protocol support — blocked by hardware availability (5 SP)\n\n**Release Checklist:**\n- ✅ All tests passing (528/528)\n- ✅ Security scan clean\n- ✅ Staging deploy script validated\n- ⬜ Staging smoke test (post-deploy)\n- ⬜ Product sign-off`,
      relatedRecords: [
        { label: 'Sprint Duration', value: 'April 1-18, 2026 (2 weeks)' },
        { label: 'Team', value: '4 engineers + 1 QA' },
        { label: 'Velocity Trend', value: '42 → 45 → 47 SP (improving)' },
        { label: 'Test Coverage', value: '528 tests passing, 78% line coverage' },
      ],
      riskFactors: ['1 story deferred (DNP3 blocked)', 'Staging deploy during active development'],
      userQuery: 'Sprint 14 is wrapping up — close the sprint and prepare v2.4.0 for staging',
      reasoningChain: [
        { step: 1, action: 'Retrieved Sprint 14 board status', result: '18 stories done, 1 blocked (DNP3 — hardware not available), 0 in progress', toolUsed: 'ext.jira.getSprint' },
        { step: 2, action: 'Calculated sprint velocity', result: '47 story points completed (target: 45) — 104% of target', toolUsed: 'ext.jira.getMetrics' },
        { step: 3, action: 'Ran release checklist validation', result: '528/528 tests passing, security scan clean, deploy script validated', toolUsed: 'ext.github.getChecks' },
        { step: 4, action: 'Verified blocked story rationale', result: 'DNP3 protocol testing requires Schweitzer SEL relay — delivery expected April 25', toolUsed: 'ext.jira.getIssue' },
        { step: 5, action: 'Prepared release notes and staging deploy', result: 'v2.4.0 release with SCADA MVP + 3 bug fixes, staging deploy ready', toolUsed: 'llm_reasoning' },
      ],
      confidence: 0.95,
      alternativesConsidered: [
        'Wait for DNP3 story completion — rejected: hardware not available until April 25, blocks entire release',
        'Skip staging, deploy to production — rejected: staging smoke test required per release process',
      ],
      sourcesCited: [
        'Jira: Sprint 14 board',
        'GitHub: CI/CD pipeline results (all green)',
        'Release Process: RP-001 (staging required before production)',
      ],
      contextHistory: [
        { timestamp: '2026-04-01T09:00:00Z', type: 'note', title: 'Sprint 14 started', description: 'Goal: SCADA connector MVP + bug fix batch. 19 stories, 50 SP committed.', actor: 'Scrum Master' },
        { timestamp: '2026-04-10T14:00:00Z', type: 'note', title: 'DNP3 story blocked', description: 'Schweitzer SEL relay needed for testing — delivery delayed to April 25', actor: 'David Park' },
        { timestamp: '2026-04-17T16:00:00Z', type: 'commit', title: 'SCADA connector MVP merged', description: 'OPC-UA + Modbus support merged to main. 13 story points.', actor: 'Engineering' },
        { timestamp: '2026-04-18T08:00:00Z', type: 'note', title: 'All tests passing', description: '528/528 tests green. Security scan: 0 critical, 0 high findings.', actor: 'CI/CD' },
      ],
      contextSummary: [
        { label: 'Sprint', value: 'Sprint 14 (April 1-18, 2026)' },
        { label: 'Release', value: 'v2.4.0 → staging' },
        { label: 'Velocity', value: '47 SP completed (target: 45)' },
        { label: 'Stories', value: '18/19 done, 1 deferred (DNP3 blocked)' },
        { label: 'Tests', value: '528/528 passing, 78% coverage' },
        { label: 'Security', value: 'Clean — 0 critical, 0 high findings' },
      ],
    },
    level: 'single',
    status: 'pending',
    priority: 'medium',
    riskScore: 0.2,
    requestedBy: 'Engineering Lead',
    requestedAt: hoursAgo(2),
    expiresAt: hoursFromNow(12),
  },
];

export const MOCK_HISTORY_APPROVALS: ApprovalRequest[] = [
  {
    id: 'apr-h1',
    type: 'tool_call',
    agent: { id: 'agent-maintenance', name: 'Maintenance Intelligence', icon: 'wrench', iconColor: 'cyan' },
    toolName: 'iq.cmms.updatePMSchedule',
    toolDisplayName: 'Update PM Schedule',
    action: { title: 'Reschedule PM-2847 from Apr 20 to Apr 18', description: 'Moved up preventive maintenance based on trending vibration data.' },
    evidence: { parameters: { pmId: 'PM-2847', oldDate: '2026-04-20', newDate: '2026-04-18', reason: 'Vibration trending up' } },
    level: 'single',
    status: 'approved',
    priority: 'low',
    riskScore: 0.2,
    requestedBy: 'Jay Talreja',
    requestedAt: hoursAgo(26),
    expiresAt: hoursAgo(22),
    decidedBy: 'Admin',
    decidedAt: hoursAgo(25),
    decisionReason: 'Good catch — approved immediately',
  },
  {
    id: 'apr-h2',
    type: 'purchase_order',
    agent: { id: 'agent-parts', name: 'Parts & Inventory', icon: 'database', iconColor: 'teal' },
    toolName: 'ext.sap.createPurchaseOrder',
    toolDisplayName: 'Create Purchase Order',
    action: { title: 'Order replacement valve FV-401', description: 'Emergency replacement for failed control valve.' },
    evidence: { parameters: { vendor: 'Fisher Controls', partNumber: 'FV-401-SS', quantity: 1, totalAmount: 4850.00 } },
    level: 'supervisor',
    status: 'approved',
    priority: 'high',
    riskScore: 0.6,
    requestedBy: 'Mike Chen',
    requestedAt: hoursAgo(48),
    expiresAt: hoursAgo(40),
    decidedBy: 'Sarah Johnson',
    decidedAt: hoursAgo(47),
    decisionReason: 'Emergency repair — approved per ERP-2026-0012',
  },
  {
    id: 'apr-h3',
    type: 'email_draft',
    agent: { id: 'agent-sales', name: 'Sales Assistant', icon: 'mail', iconColor: 'violet' },
    toolName: 'm365.outlook.sendEmail',
    toolDisplayName: 'Send Email',
    action: { title: 'Quote follow-up to Delta Corp', description: 'AI-drafted pricing clarification email.' },
    evidence: { parameters: { recipient: 'procurement@deltacorp.com', subject: 'Re: Quote Q-2026-0134' } },
    level: 'single',
    status: 'rejected',
    priority: 'low',
    riskScore: 0.25,
    requestedBy: 'Sales Team',
    requestedAt: hoursAgo(72),
    expiresAt: hoursAgo(71),
    decidedBy: 'Admin',
    decidedAt: hoursAgo(71.5),
    decisionReason: 'Pricing was outdated — need to update quote first',
  },
  {
    id: 'apr-h4',
    type: 'tool_call',
    agent: { id: 'agent-compliance', name: 'Compliance Monitor', icon: 'shield-check', iconColor: 'violet' },
    toolName: 'iq.compliance.updateCertification',
    toolDisplayName: 'Update Certification',
    action: { title: 'Update CMMC L2 certification expiry', description: 'Extended certification date based on successful reassessment.' },
    evidence: { parameters: { framework: 'CMMC Level 2', oldExpiry: '2026-06-30', newExpiry: '2027-06-30' } },
    level: 'two_person',
    status: 'approved',
    priority: 'high',
    riskScore: 0.7,
    requestedBy: 'Compliance Team',
    requestedAt: hoursAgo(96),
    expiresAt: hoursAgo(88),
    decidedBy: 'Admin + Sarah Johnson',
    decidedAt: hoursAgo(92),
  },
];
